import type { ChatSessionSnapshot } from '@/lib/chat-session-snapshot'
import type { ChatImage, DocumentReference, Message, AskQuestionData, FlashCardSessionData } from '@/components/layout/chat-pane.types'
import { toolMessagesFromTrace } from '@/lib/chat-history-for-stream'
import { pendingProposedEditsFromMetadata } from '@/lib/pending-proposed-edits'
import {
  inferCappedAtFromAssistantContent,
  isAgenticContinueUserText,
  stripAgenticPauseProse,
} from '@/components/layout/chat-pane.utils'

export type ApiConversationMessage = {
  id?: string
  role: string
  content?: string
  references?: unknown
  metadata?: Record<string, unknown>
  edit_of?: string
}

type TouchedFileRecord = {
  documentId?: string
  /** Internal storage path (e.g. ws/{orgId}/{slug}.md) — never render directly; use displayPath. */
  filePath: string
  /** Human-facing "{workspaceSlug}/{docSlug}" path from the backend, when available. */
  displayPath?: string | null
  /** Full shareable webapp URL, when available. */
  url?: string | null
  title?: string
  linesAdded?: number
  linesRemoved?: number
  changeStatus?: string
}

/** Map API conversation rows to chat Message[] (user/assistant only; filters continue stubs). */
export function mapApiMessagesToChatMessages(messages: ApiConversationMessage[]): Message[] {
  const out: Message[] = []
  for (const msg of messages || []) {
    if (msg.role !== 'user' && msg.role !== 'assistant') continue
    if (msg.role === 'user' && isAgenticContinueUserText(msg.content ?? '')) continue
    const mapped = mapSingleApiMessage(msg)
    const pendingCards: Message[] =
      msg.role === 'assistant'
        ? pendingProposedEditsFromMetadata(msg.metadata).map((edit) => ({
            role: 'assistant' as const,
            content: '',
            proposedEdit: edit,
            timestamp: new Date(),
          }))
        : []
    // Rebuild in-memory tool rows from persisted toolTrace so Continue / follow-ups
    // can replay after reload (ai_messages CHECK still excludes role=tool).
    // Strip toolTrace from the assistant after expand so serialize does not double-emit.
    if (mapped.role === 'assistant' && mapped.metadata?.toolTrace) {
      out.push(...toolMessagesFromTrace(mapped.metadata.toolTrace))
      const { toolTrace: _trace, ...restMeta } = mapped.metadata
      out.push(...pendingCards)
      out.push({
        ...mapped,
        metadata: Object.keys(restMeta).length ? restMeta : undefined,
      })
      continue
    }
    out.push(...pendingCards)
    out.push(mapped)
  }
  return clearResolvedAgenticCaps(out)
}

/**
 * After Continue, live UI clears `cappedAt` on prior assistants, but the DB row still has
 * `metadata.cappedAt`. On reload that resurrected the Continue button mid-thread.
 * If a later assistant turn exists, the cap was already continued - drop the CTA.
 */
export function clearResolvedAgenticCaps(messages: Message[]): Message[] {
  let hasLaterAssistant = false
  const next = [...messages]
  for (let i = next.length - 1; i >= 0; i -= 1) {
    const m = next[i]
    if (m?.role === 'assistant') {
      if (hasLaterAssistant && m.cappedAt) {
        next[i] = { ...m, cappedAt: undefined }
      }
      hasLaterAssistant = true
    }
  }
  return next
}

/** Rebuild composer/stream image slots from persisted attachment IDs (no base64). */
export function imagesFromAttachmentIds(raw: unknown): ChatImage[] | undefined {
  if (!Array.isArray(raw)) return undefined
  const ids = [...new Set(raw.map((id) => String(id || '').trim()).filter(Boolean))]
  if (!ids.length) return undefined
  return ids.map((id) => ({
    id: `att-${id}`,
    attachmentId: id,
    dataUrl: `/api/attachments/${id}/image`,
    base64: '',
    mediaType: 'image/jpeg',
  }))
}

function mapSingleApiMessage(msg: ApiConversationMessage): Message {
  const metadata = msg.metadata ?? {}
  const images = imagesFromAttachmentIds(metadata.attachmentIds)

  // Do not invent proposedEdit from metadata.touchedFiles. Those rows power the
  // completed-files bar only. Pending review bodies live in metadata.pendingProposedEdits.
  const cappedAtFromMeta =
    metadata.cappedAt === 'max_tools' ||
    metadata.cappedAt === 'max_rounds' ||
    metadata.cappedAt === 'incomplete_write'
      ? (metadata.cappedAt as 'max_tools' | 'max_rounds' | 'incomplete_write')
      : undefined
  const rawContent = msg.content ?? ''
  const cappedAt =
    cappedAtFromMeta ??
    (msg.role === 'assistant' ? inferCappedAtFromAssistantContent(rawContent) : undefined)
  const content =
    msg.role === 'assistant' ? stripAgenticPauseProse(rawContent) : rawContent

  return {
    role: msg.role as 'user' | 'assistant',
    content,
    references: msg.references as DocumentReference[] | undefined,
    images,
    messageId: msg.id != null ? String(msg.id) : undefined,
    aborted: metadata.status === 'aborted',
    metadata,
    cappedAt,
    editOf: msg.edit_of != null ? String(msg.edit_of) : undefined,
    askQuestion: askQuestionFromMetadata(metadata.askQuestion),
    flashCardSession: flashCardFromMetadata(metadata.flashCardSession),
  }
}

function askQuestionFromMetadata(raw: unknown): AskQuestionData | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const row = raw as Record<string, unknown>
  if (typeof row.question !== 'string' || !Array.isArray(row.options)) return undefined
  const options = row.options
    .filter((opt) => opt && typeof opt === 'object')
    .map((opt) => {
      const o = opt as Record<string, unknown>
      return {
        id: String(o.id || ''),
        label: String(o.label || ''),
        description: typeof o.description === 'string' ? o.description : undefined,
      }
    })
    .filter((opt) => opt.id && opt.label)
  if (!options.length) return undefined
  let answered: AskQuestionData['answered']
  if (row.answered && typeof row.answered === 'object') {
    const a = row.answered as Record<string, unknown>
    if (typeof a.summary === 'string') {
      answered = {
        selectedIds: Array.isArray(a.selectedIds) ? a.selectedIds.map(String) : [],
        summary: a.summary,
        otherText: typeof a.otherText === 'string' ? a.otherText : undefined,
      }
    }
  }
  return {
    questionId: String(row.questionId || 'q'),
    question: row.question,
    options,
    allowMultiple: row.allowMultiple === true,
    allowOther: row.allowOther !== false,
    replyTemplate: typeof row.replyTemplate === 'string' ? row.replyTemplate : undefined,
    answered,
  }
}

function flashCardFromMetadata(raw: unknown): FlashCardSessionData | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const row = raw as Record<string, unknown>
  if (!Array.isArray(row.cards) || row.cards.length === 0) return undefined
  return raw as FlashCardSessionData
}

/** Build metadata.touchedFiles payload for assistant message persistence. */
export function buildTouchedFilesMetadata(
  files: Array<{
    filePath: string
    displayPath?: string | null
    url?: string | null
    documentId?: string
    title?: string
    linesAdded?: number
    linesRemoved?: number
    changeStatus?: string
    status?: 'new' | 'modified'
  }>,
): Record<string, unknown> | undefined {
  if (!files.length) return undefined
  return {
    touchedFiles: files.map((f) => ({
      documentId: f.documentId,
      filePath: f.filePath,
      displayPath: f.displayPath,
      url: f.url,
      title: f.title,
      linesAdded: f.linesAdded,
      linesRemoved: f.linesRemoved,
      changeStatus: f.changeStatus,
      status: f.status,
    })),
  }
}

/** Completed-files bar state from hydrated assistant messages. */
export function completedFilesFromMessages(messages: Message[]): ChatSessionSnapshot['completedFiles'] {
  const out: ChatSessionSnapshot['completedFiles'] = []
  for (const m of messages) {
    const touched = m.metadata?.touchedFiles
    if (Array.isArray(touched)) {
      for (const f of touched) {
        const row = f as TouchedFileRecord & { status?: 'new' | 'modified' }
        if (!row?.filePath) continue
        // Pending/rejected proposals must not appear as completed writes.
        if (row.changeStatus === 'pending' || row.changeStatus === 'rejected') continue
        out.push({
          filePath: row.filePath,
          displayPath: row.displayPath,
          url: row.url,
          documentId: row.documentId,
          title: row.title,
          linesAdded: row.linesAdded ?? 0,
          linesRemoved: row.linesRemoved ?? 0,
          changeStatus: row.changeStatus,
          status: row.status === 'new' ? 'new' : 'modified',
        })
      }
    }

    // Live tool rows (createDocument etc.) - recover "Filed" creates into the Files bar
    // even when thinking_start cleared session state or metadata lagged.
    if (m.role === 'tool' && m.toolResult && typeof m.toolResult === 'object') {
      const tr = m.toolResult as Record<string, unknown>
      if (tr.success !== true) continue
      const name = m.toolName || ''
      const isCreate =
        name === 'createDocument' || name === 'duplicateFlow' || name === 'createSkill'
      const isWrite =
        isCreate ||
        name === 'updateDocument' ||
        name === 'appendToDocument' ||
        name === 'patchDocument' ||
        name === 'moveSection'
      if (!isWrite) continue
      // Proposed edits are not applied until Accept - skip until then.
      if (tr.type === 'proposed_edit') continue
      const filePath =
        (typeof tr.filePath === 'string' && tr.filePath) ||
        (typeof tr.filename === 'string' && tr.filename) ||
        ''
      if (!filePath) continue
      const content = typeof tr.content === 'string' ? tr.content : ''
      const linesFromContent = content ? content.split('\n').length : 0
      out.push({
        filePath,
        displayPath: typeof tr.displayPath === 'string' ? tr.displayPath : null,
        url: typeof tr.url === 'string' ? tr.url : null,
        documentId: typeof tr.documentId === 'string' ? tr.documentId : undefined,
        title: typeof tr.title === 'string' ? tr.title : undefined,
        linesAdded:
          typeof tr.linesAdded === 'number' ? tr.linesAdded : linesFromContent || (isCreate ? 1 : 0),
        linesRemoved: typeof tr.linesRemoved === 'number' ? tr.linesRemoved : 0,
        status: isCreate ? 'new' : 'modified',
      })
    }
  }
  return out
}
